/** Segmented tabs for picking one of a few values, such as the run mode, a filter or the channel. */

import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: string;
  icon?: LucideIcon;
  /** Draw the icon alone; the label still names the tab for assistive tech and on hover. */
  iconOnly?: boolean;
  /** A number after the label, such as how many items the tab holds. */
  count?: number;
}

interface SegmentedProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
}

// Sizes and colours come from the Segmented Tabs design: 26px pills inside a 2px
// track, 12px/16px medium text. The selected pill is the list's sliding indicator,
// so a trigger paints no background of its own.
const TRIGGER_CLASSES = cn(
  'group h-6.5 flex-none gap-2 border-none px-3 text-xs leading-4 font-medium',
  'text-gray-900 transition-[color,box-shadow] duration-200 hover:text-text-heading data-[state=active]:text-text-heading'
);

const LIST_CLASSES = cn(
  'h-min gap-0 rounded-full bg-gray-300 p-0.5',
  '[&>span[aria-hidden]]:border-gray-600 [&>span[aria-hidden]]:bg-white',
  '[&>span[aria-hidden]]:shadow-[0px_3px_6px_rgba(0,0,0,0.2)] [&>span[aria-hidden]]:backdrop-blur-[6px]'
);

export const Segmented = <T extends string>({ options, value, onChange, disabled, className }: SegmentedProps<T>) => (
  // The tabs primitive speaks in strings; every value it can hand back is one of the options.
  <Tabs variant='segmented' value={value} onValueChange={(v) => !disabled && onChange(v as T)} className={className}>
    <TabsList className={LIST_CLASSES}>
      {options.map(({ value: optionValue, label, icon: Icon, iconOnly, count }) => {
        const isSelected = value === optionValue;
        return (
          <TabsTrigger
            key={optionValue}
            value={optionValue}
            disabled={disabled}
            title={iconOnly ? label : undefined}
            className={cn(
              TRIGGER_CLASSES,
              iconOnly && 'px-2',
              !isSelected && 'hover:inset-ring-1 hover:inset-ring-gray-600'
            )}
          >
            {Icon && (
              <Icon
                // 1.5 on Lucide's 24px grid renders as the design's 1.125 stroke at 18px.
                strokeWidth={1.5}
                aria-hidden
                className={cn(
                  'size-4.5 shrink-0 transition-transform duration-200 ease-out',
                  !isSelected && 'group-hover:scale-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                )}
              />
            )}
            {iconOnly ? <span className='sr-only'>{label}</span> : label}
            {count !== undefined && <span className='tabular-nums text-gray-800'>{count}</span>}
          </TabsTrigger>
        );
      })}
    </TabsList>
  </Tabs>
);
